import React, { FC, useState } from 'react'
import { useRouter } from 'next/router'
import { Image } from '@/atoms/Image'
import { ExperimentVariation } from '@/experimentation'
import { FadeInExperiment } from '@/experimentation/components/FadeInExperiment'
import { YoutubeVideoModal } from '@/molecules/YoutubeVideoModal'
import { TheatricalPromoType } from '@/services/Theatrical/contentfulQueries'
import { ReservationObject, TheatricalReleaseObject } from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'
import { DiscountType, useTicketsContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsContext'
import ChangeReservationBanner from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/ChangeReservationBanner'
import { TicketPromoBanner } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/DiscountBanner'
import { BraveTheDarkDiscountBanner } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/DiscountBanner/BraveTheDarkDiscountBanner'
import { GuildTicketsBanner } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/GuildTicketsBanner'
import { isAngelIntegratedCountry } from '@/views/TicketCheckoutViews/ShowtimesView/utils/utils'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'

interface BannerSectionProps {
  reservation?: ReservationObject
  isEditReservationFlow: boolean
  theatricalRelease: TheatricalReleaseObject
  promo?: TheatricalPromoType
  calendarRef?: React.RefObject<HTMLDivElement>
}

const TicketBannerSection: FC<BannerSectionProps> = ({
  promo,
  reservation,
  isEditReservationFlow,
  theatricalRelease,
  calendarRef,
}) => {
  const { t } = useTranslate('tickets')
  const { discount } = useTicketsContext()
  const { query } = useRouter()
  const { areGuildTicketsAvailable, discountCodes } = useGuildTickets(theatricalRelease?.theatricalSlug)
  const countryCode = theatricalRelease?.region?.countryCode
  const [showModal, setShowModal] = useState(false)

  const handleClick = () => setShowModal(true)

  return (
    <>
      {reservation?.id && !isEditReservationFlow && isAngelIntegratedCountry(countryCode) && (
        <TicketPromoBanner
          theatricalSlug={theatricalRelease?.theatricalSlug}
          className="hidden w-full md:flex"
          promo={promo}
          discount={discount as DiscountType}
          calendarRef={calendarRef}
        />
      )}

      {reservation?.priorReservation?.id &&
        reservation?.isChangeReservation &&
        isAngelIntegratedCountry(countryCode) && (
          <ChangeReservationBanner
            description={t('pleaseSelectANewShowtimeBelow', 'Please select a new showtime below.')}
            priorReservation={reservation?.priorReservation}
            step={1}
          />
        )}
      {areGuildTicketsAvailable && (
        <GuildTicketsBanner
          discountCodes={discountCodes}
          projectName={theatricalRelease?.title ?? ''}
          theatricalSlug={theatricalRelease?.theatricalSlug}
          className="overflow-hidden lg:hidden"
        />
      )}
      {theatricalRelease?.theatricalSlug === 'brave-the-dark' && query?.discounts !== 'brave20' && (
        <FadeInExperiment experimentName="btd-giveaway-vs-discount" defaultValue={false}>
          <ExperimentVariation variation={true}>
            <div className="hidden md:block">
              <BraveTheDarkDiscountBanner />
            </div>
          </ExperimentVariation>
          <ExperimentVariation default>
            <div className="hidden cursor-pointer md:block lg:hidden" onClick={handleClick}>
              <Image
                className="h-full w-full"
                src={`/v1736965054/brave-the-dark/dream-vacation-banner-tablet-3x-01.png`}
                alt=""
                width={1200}
                height={300}
              />
            </div>
            <div className="hidden cursor-pointer lg:block" onClick={handleClick}>
              <Image
                className="h-full w-full"
                src={`/v1736965054/brave-the-dark/dream-vacation-banner-desk-3x-01.png`}
                alt=""
                width={2000}
                height={334}
              />
            </div>
            <YoutubeVideoModal
              youTubeUrl="https://www.youtube-nocookie.com/embed/knvKDPwWo3I"
              showModal={showModal}
              handleClose={() => setShowModal(false)}
              className="!z-[60]"
            />
          </ExperimentVariation>
        </FadeInExperiment>
      )}
      {theatricalRelease?.theatricalSlug === 'brave-the-dark' && query?.discounts === 'brave20' && (
        <div className="hidden md:block">
          <BraveTheDarkDiscountBanner />
        </div>
      )}
    </>
  )
}

export default TicketBannerSection
